import React, { useState } from "react";
import {
  Mail,
  Phone,
  ArrowRight,
  Wifi,
  AlertTriangle,
  Smartphone,
  Globe,
  MessageCircle,
  Shield } from "lucide-react";
import { ContactPageSEO } from "../components/SEO";
import "./ContactPage.css";
import emailjs from "@emailjs/browser";

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    consent: false
  });

  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    const templateParams = {
      from_name: formData.name,
      from_email: formData.email,
      phone: formData.phone,
      message: formData.message,
    };

    console.log("Sending email with data:", templateParams);

    try {
      // 1️⃣ Send to YOU (business owner)
      const result = await emailjs.send(
        "service_vj4h97g",           // Your EmailJS Service ID
        "template_gjqulg1",     // ⬅️ NEW template for contact form
        templateParams,
        "vWkJpQqvu_4nMnMTT"         // Your EmailJS Public Key
      );

      console.log("Email sent successfully:", result);

      // 2️⃣ (Optional) Send auto-reply to customer
      await emailjs.send(
        "service_096xkyv",
        "template_gjqulg1",    // ⬅️ NEW auto-reply template
        templateParams,
        "vWkJpQqvu_4nMnMTT"
      );

      setSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "", consent: false });

      setTimeout(() => {
        setSubmitted(false);
      }, 5000);

    } catch (error) {
      console.error("Email send error:", error);
      alert("Failed to send message. Please try again or call us directly.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="contact-page">
      <ContactPageSEO />

      {/* Main Content */}
      <section className="contact-hero">
        <div className="hero-decoration"></div>
        <div className="container contact-container">
          <div className="contact-form-section">
            <div className="form-header">
              <h1 className="form-title">Get <span className="accent">in Touch</span></h1>
              <div className="header-line"></div>
            </div>

            {submitted && (
              <div className="success-message">
                <p>✅ Thank you! We've received your message and will get back to you soon.</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="contact-form">
              <div className="form-group">
                <label htmlFor="name" className="form-label">Your Name *</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="John Green"
                  required
                  disabled={sending}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">Your E-mail *</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="john@example.com"
                  required
                  disabled={sending}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone" className="form-label">Phone *</label>
                <input
                  id="phone"
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="form-input"
                  placeholder="0800 123 456"
                  required
                  disabled={sending}
                />
              </div>

              <div className="form-group">
                <label htmlFor="message" className="form-label">Your Message *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className="form-textarea"
                  placeholder="Tell us how we can help..."
                  rows="4"
                  required
                  disabled={sending}
                ></textarea>
              </div>

              <button 
                type="submit" 
                className="submit-btn"
                disabled={sending}
              >
                <span>{sending ? "Sending..." : "Submit"}</span>
                <ArrowRight className="btn-icon" />
              </button>
            </form>
          </div>

          {/* Telkom Support Section */}
          <div className="telkom-support">
            <div className="fraud-alert">
              🚨 <strong>Fraud Hotline:</strong> Report unethical activity confidentially — 
              Call <span>069 382 1773</span> or Email <span>ops.augustco@absamail.co.za</span>
            </div>

            <h2 className="section-title">Telkom <span className="highlight">Support & Service Lines</span></h2>
            <div className="support-grid">
              <div className="support-card">
                <Phone className="support-icon" />
                <p><strong>Fixed Line Technical & Billing:</strong> Call <a href="tel:10210">10210</a></p>
              </div>

              <div className="support-card">
                <Smartphone className="support-icon" />
                <p><strong>Mobile Queries:</strong> Call <a href="tel:081180">081 180</a></p>
              </div>

              <div className="support-card">
                <Wifi className="support-icon" />
                <p><strong>Openserve Fibre Support:</strong> Call <a href="tel:0800007268">0800 007 268</a></p>
              </div>

              <div className="support-card">
                <Globe className="support-icon" />
                <p><strong>Fault Reporting:</strong> <a href="https://apps.telkom.co.za/logafault" target="_blank" rel="noopener noreferrer">Log a fault here</a></p>
              </div>

              <div className="support-card">
                <Shield className="support-icon" />
                <p><strong>Log via Telkom App:</strong> Download the Telkom App to report faults.</p>
              </div>

              <div className="support-card">
                <MessageCircle className="support-icon" />
                <p><strong>SMS Fault:</strong> Send SERVICE + [10-digit number] to <strong>30591</strong></p>
              </div>

              <div className="support-card">
                <AlertTriangle className="support-icon" />
                <p><strong>Internet Username Reset:</strong> SMS Password to <strong>0123210215</strong></p>
              </div>

              <div className="support-card">
                <Mail className="support-icon" />
                <p><strong>Balance Enquiries:</strong> SMS your landline number + ID to <strong>0123210210</strong></p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}